<template>
  <div class="linexo details">
    <div class="details__header">
      <AHeadline text="details.headline" type="h3" />
      <LButton variant="tertiary" prepend-icon="lnx-close" @click="onDetailButtonClose">
        {{ $t('details.close') }}
      </LButton>
    </div>

    <LTable class="details__table details__table--mobile" align="center">
      <LTHead>
        <LTR>
          <LTH>{{ $t('details.private_buy') }}</LTH>
          <LTH>{{ $t('details.company_bike') }}</LTH>
        </LTR>
      </LTHead>

      <LTBody>
        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.gross_salary') }}</LTH>
        </LTR>
        <LTR>
          <LTD>{{ getDisplayedPrice(privateBuy.grossSalary, { showNull: true }) }}</LTD>
          <LTD>{{ getDisplayedPrice(leasing.grossSalary, { showNull: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.employer_subsidy') }}</LTH>
        </LTR>
        <LTR>
          <LTD>-</LTD>
          <LTD>{{ getDisplayedPrice(leasing.employerSubsidy, { showNull: true, forcePrefix: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.leasing_rate') }}</LTH>
        </LTR>
        <LTR>
          <LTD>-</LTD>
          <LTD>{{ getDisplayedPrice(leasing.leasingRate, { negate: true, showNull: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.insurance_premium') }}</LTH>
        </LTR>
        <LTR>
          <LTD>-</LTD>
          <LTD>{{ getDisplayedPrice(leasing.insuranceBonus, { negate: true, showNull: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.monetary_advantage') }}</LTH>
        </LTR>
        <LTR>
          <LTD>-</LTD>
          <LTD>{{ getDisplayedPrice(leasing.monetaryAdvantage, { showNull: true, forcePrefix: true }) }}</LTD>
        </LTR>

        <LTR variant="highlight" text-style="highlight">
          <LTH colspan="2">{{ $t('details.calculation_basis') }}</LTH>
        </LTR>
        <LTR variant="highlight" text-style="highlight">
          <LTD>{{ getDisplayedPrice(privateBuy.calculationBasis, { showNull: true }) }}</LTD>
          <LTD>{{ getDisplayedPrice(leasing.calculationBasis, { showNull: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.tax_deductions') }}</LTH>
        </LTR>
        <LTR>
          <LTD>{{ getDisplayedPrice(privateBuy.tax, { showNull: true, negate: true }) }}</LTD>
          <LTD>{{ getDisplayedPrice(leasing.tax, { showNull: true, negate: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.social_insurance_deductions') }}</LTH>
        </LTR>
        <LTR>
          <LTD>{{ getDisplayedPrice(privateBuy.socialInsurance, { showNull: true, negate: true }) }}</LTD>
          <LTD>{{ getDisplayedPrice(leasing.socialInsurance, { showNull: true, negate: true }) }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.monetary_advantage') }}</LTH>
        </LTR>
        <LTR>
          <LTD>-</LTD>
          <LTD>{{ getDisplayedPrice(leasing.monetaryAdvantage, { showNull: true, negate: true }) }}</LTD>
        </LTR>

        <LTR text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.net_payout') }}</LTH>
        </LTR>
        <LTR text-style="highlight">
          <LTD>{{ privateBuy.netPayout ? formatPrice(privateBuy.netPayout) : '\u00A0' }}</LTD>
          <LTD>{{ leasing.netPayout ? formatPrice(leasing.netPayout) : '\u00A0' }}</LTD>
        </LTR>
      </LTBody>

      <LTFoot>
        <LTR>
          <LTH colspan="2" align="center">
            <template #overline>
              <span class="details__footer-overline">{{ $t('details.monthly_net_burden') }}</span>
            </template>
            <span class="details__footer-price">{{ formatPrice(price) }}</span>
          </LTH>
        </LTR>
      </LTFoot>
    </LTable>

    <LTable class="details__table-bottom details__table--mobile" align="center">
      <LTHead>
        <LTR>
          <LTH>{{ $t('details.private_buy') }}</LTH>
          <LTH>{{ $t('details.company_bike') }}</LTH>
        </LTR>
      </LTHead>
      <LTBody>
        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.net_burden_duration') }}</LTH>
        </LTR>
        <LTR>
          <LTD>-</LTD>
          <LTD>{{ leasing.netBurdenDuration ? formatPrice(leasing.netBurdenDuration) : '\u00A0' }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.acquisition_price') }}</LTH>
        </LTR>
        <LTR>
          <LTD>{{ privateBuy.purchasePrice ? formatPrice(privateBuy.purchasePrice) : '\u00A0' }}</LTD>
          <LTD>{{ $t('details.included') }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.insurance_cost') }}</LTH>
        </LTR>
        <LTR>
          <LTD>{{ privateBuy.insuranceCost ? formatPrice(privateBuy.insuranceCost) : '\u00A0' }}</LTD>
          <LTD>{{ $t('details.included') }}</LTD>
        </LTR>

        <LTR variant="subtle" text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.expected_used_purchase_price') }}</LTH>
        </LTR>
        <LTR variant="subtle">
          <LTD>-</LTD>
          <LTD>{{ leasing.expectedUsedPurchasePrice ? formatPrice(leasing.expectedUsedPurchasePrice) : '\u00A0' }}</LTD>
        </LTR>

        <LTR text-style="default-semibold">
          <LTH colspan="2">{{ $t('details.total_cost') }}</LTH>
        </LTR>
        <LTR variant="highlight" text-style="highlight">
          <LTD>{{ privateBuy.totalCost ? formatPrice(privateBuy.totalCost) : '\u00A0' }}</LTD>
          <LTD>{{ leasing.totalCost ? formatPrice(leasing.totalCost) : '\u00A0' }}</LTD>
        </LTR>
      </LTBody>

      <LTFoot>
        <LTR>
          <LTH colspan="2" align="center">
            <template #overline>
              <span class="details__footer-overline">{{ $t('details.total_savings') }}</span>
            </template>
            <span class="details__footer-price">
              {{ formatPrice(savings) }}
              <span class="details__footer-percentage"> ({{ formatPercentage(savingsPercentage) }})</span>
            </span>
          </LTH>
        </LTR>
      </LTFoot>
    </LTable>
  </div>
</template>

<script lang="ts" setup>
import AHeadline from '@/components/atoms/content/headline/A-Headline.vue'
import { LButton, LTable, LTHead, LTBody, LTFoot, LTR, LTH, LTD } from '@wgg.hyve/hyve-ui-library'
import { onMounted } from 'vue'
import { formatPercentage, formatPrice, getDisplayedPrice, scrollToElement } from '@/services/helper.service.ts'

type Props = {
  price: number
  savings: number
  savingsPercentage: number
  showDetailsTable: boolean
  privateBuy: {
    grossSalary: number | null
    calculationBasis: number | null
    tax: number | null
    socialInsurance: number | null
    netPayout: number | null
    purchasePrice: number | null
    insuranceCost: number | null
    totalCost: number | null
  }
  leasing: {
    grossSalary: number | null
    employerSubsidy: number | null
    leasingRate: number | null
    insuranceBonus: number | null
    monetaryAdvantage: number | null
    calculationBasis: number | null
    tax: number | null
    socialInsurance: number | null
    netPayout: number | null
    netBurdenDuration: number | null
    expectedUsedPurchasePrice: number | null
    totalCost: number | null
  }
}

const props = defineProps<Props>()

const emit = defineEmits(['update:showDetailsTable'])

onMounted(() => {
  scrollToElement('.details__header', 48)
})

async function onDetailButtonClose() {
  await scrollToElement('#lnx-calculator', 48)
  emit('update:showDetailsTable', false)
}
</script>

<style lang="scss">
@import 'm-detail-table.scss';
</style>
